import React, { useEffect, useState } from 'react';
import { FiFileText, FiCheckSquare, FiRefreshCw } from 'react-icons/fi';
import { useCallConclusion, type InsuredRecord } from './useCallConclusion';
import CallConclusionFields from './CallConclusionFields';

interface CallSummaryProps {
  parentCallSid: string;
  insuredId: string;
  callDuration: string;
  phoneNumber?: string;
  contactName?: string;
  onSkipButtonClick: () => void;
  onInsuredSelected: (insured: InsuredRecord) => void;
}

const CallSummary: React.FC<CallSummaryProps> = ({ parentCallSid, insuredId, callDuration, phoneNumber, contactName, onSkipButtonClick, onInsuredSelected }) => {
  const [transcript, setTranscript] = useState('');
  const [callSummary, setCallSummary] = useState('');
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [callNotesLoading, setCallNotesLoading] = useState(false);
  const [callSummaryLoading, setCallSummaryLoading] = useState(false);

  const conclusion = useCallConclusion({
    parentCallSid,
    initialInsuredId: insuredId,
    initialContactName: contactName,
    transcript,
    summary: callSummary,
    phoneNumber,
    onInsuredSelected,
    onSaved: onSkipButtonClick,
  });

  const getTranscript = () => {
    setTranscriptLoading(true);
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/transcript?ParentCallSid=${encodeURIComponent(parentCallSid)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { if (data?.data != null) setTranscript(data.data); })
      .catch(() => {})
      .finally(() => setTranscriptLoading(false));
  };

  const getSummary = () => {
    setCallSummaryLoading(true);
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/summary?ParentCallSid=${encodeURIComponent(parentCallSid)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { if (data?.data != null) setCallSummary(data.data); })
      .catch(() => {})
      .finally(() => setCallSummaryLoading(false));
  };

  const getCallNotes = () => {
    setCallNotesLoading(true);
    fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/notes?ParentCallSid=${encodeURIComponent(parentCallSid)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data?.data)) {
          conclusion.setCallNotes(data.data.map((note: string, i: number) => `${i + 1}. ${note}`).join('\n'));
        }
      })
      .catch(() => {})
      .finally(() => setCallNotesLoading(false));
  };

  const generateAll = () => {
    getTranscript();
    getSummary();
    getCallNotes();
  };

  useEffect(() => {
    const delay = setTimeout(generateAll, 5000);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCallSid]);

  return (
    <div className="call-summary">
      <div className="call-summary__header-row">
        <p className="call-summary__duration">Duration: {callDuration}</p>
        <button
          className="call-summary__regenerate-btn"
          onClick={generateAll}
          disabled={transcriptLoading || callSummaryLoading || callNotesLoading}
        >
          <FiRefreshCw size={12} className={(transcriptLoading || callSummaryLoading || callNotesLoading) ? 'dialer-spin' : ''} />
          Regenerate Summary
        </button>
      </div>

      <div className="call-summary__section">
        <div className="call-summary__section-header">
          <FiFileText size={13} /> Transcription
          {transcriptLoading && <FiRefreshCw size={12} className="dialer-spin" />}
        </div>
        <div className="call-summary__section-body">{transcriptLoading ? 'Generating transcript…' : (transcript || 'No transcript available yet.')}</div>
      </div>

      <div className="call-summary__section">
        <div className="call-summary__section-header">
          <FiCheckSquare size={13} /> Summary
          {callSummaryLoading && <FiRefreshCw size={12} className="dialer-spin" />}
        </div>
        <div className="call-summary__section-body">{callSummaryLoading ? 'Generating summary…' : (callSummary || 'No summary available yet.')}</div>
      </div>

      <CallConclusionFields
        conclusion={conclusion}
        callNotesLoading={callNotesLoading}
        onSkip={onSkipButtonClick}
        saveLabel="Save & Close"
      />
    </div>
  );
};

export default CallSummary;

